export interface CraftStoryContent {
  tagline: string;
  story: string;
  origin: string;
  tradition: string;
  categoryLine: string;
  teaser: string;
  icon: string;
}

const craftStoryBySlug: Record<string, CraftStoryContent> = {
  terracotta: {
    tagline: "Clay shaped by hand and finished after firing",
    story:
      "Terracotta pieces are shaped by hand, dried, and kiln-fired before painted details go on. That firing is why clay tone, smoke marks, and brush lines vary from one piece to the next.",
    origin: "Craft clusters across Bengal and Bihar",
    tradition:
      "Terracotta work has been practiced across Bengal and Bihar for thousands of years, with techniques usually taught inside families.",
    categoryLine:
      "Hand-shaped clay work finished after firing, so each piece keeps its own marks.",
    teaser: "Clay, kiln heat, and visible handwork.",
    icon: "POT",
  },
  madhubani: {
    tagline: "Mithila painting built line by line",
    story:
      "Madhubani painting began on the walls and floors of Mithila homes before moving to paper, wood, and decor. Fish, peacocks, borders, and filled patterns are still drawn by hand, so no two pieces land exactly the same way.",
    origin: "Mithila region, Bihar",
    tradition:
      "The form comes from Bihar's Mithila region and has long been made for weddings, festivals, and household rituals.",
    categoryLine:
      "Hand-drawn Mithila motifs with lines and fills that stay slightly different every time.",
    teaser: "Mithila painting moved from walls to paper, wood, and home decor.",
    icon: "PALETTE",
  },
  wooden: {
    tagline: "Carved with the grain, not against it",
    story:
      "Wooden craft pieces are cut and carved by hand, which is why you still see tool lines, softened edges, and grain changes up close. The material decides part of the final form.",
    origin: "Small woodcraft workshops across India",
    tradition:
      "Hand-carved wooden household and decorative objects remain a working craft across carving clusters in India.",
    categoryLine:
      "Hand carving leaves tool marks, grain shifts, and small asymmetries you can actually see.",
    teaser: "The wood grain stays part of the design.",
    icon: "WOOD",
  },
  sabai: {
    tagline: "Grass dried, twisted, then woven",
    story:
      "Sabai grass is first dried and twisted into rope-like strands before weaving starts. That process gives baskets and utility pieces their structure without making them feel stiff or synthetic.",
    origin: "Odisha and West Bengal",
    tradition:
      "Sabai weaving supports many women's groups in Odisha and West Bengal and remains tied to everyday household use.",
    categoryLine:
      "Dried sabai strands are twisted first, then woven into pieces meant for daily use.",
    teaser: "Natural sabai grass becomes structured only after twisting and weaving by hand.",
    icon: "GRASS",
  },
  home: {
    tagline: "Useful objects from working craft traditions",
    story:
      "This collection brings together everyday pieces made in small workshops rather than factory lines. The appeal is not perfect uniformity; it is the sense that a real hand solved a real household need.",
    origin: "Workshops across multiple craft regions",
    tradition:
      "Indian home craft traditions have long treated storage, serving, lighting, and decor as things worth making beautifully.",
    categoryLine:
      "Everyday objects made in small batches, where use and handwork matter more than perfect sameness.",
    teaser: "Household pieces that still look made, not manufactured.",
    icon: "HOME",
  },
  dokra: {
    tagline: "Lost-wax metal casting, one mold at a time",
    story:
      "Each Dokra piece starts as a wax model that is destroyed during casting, so the mold is used only once. That is why the surface, thickness, and tiny details always come out a little differently.",
    origin: "Craft belts in Chhattisgarh, Odisha, and West Bengal",
    tradition:
      "Dokra metal casting has been practiced in central and eastern India for roughly 4,000 years.",
    categoryLine:
      "A one-time wax mold means every Dokra casting is genuinely one of one.",
    teaser: "The wax model is broken during casting, so the mold cannot be reused.",
    icon: "METAL",
  },
};

const slugAliases: Record<string, keyof typeof craftStoryBySlug> = {
  terracotta: "terracotta",
  "terracotta-pottery": "terracotta",
  madhubani: "madhubani",
  "madhubani-art": "madhubani",
  wooden: "wooden",
  "wooden-crafts": "wooden",
  sabai: "sabai",
  "sabai-grass": "sabai",
  home: "home",
  "home-decor": "home",
  dokra: "dokra",
  "dokra-metal-art": "dokra",
};

const nameAliases: Record<string, keyof typeof craftStoryBySlug> = {
  Terracotta: "terracotta",
  "Terracotta & Pottery": "terracotta",
  Madhubani: "madhubani",
  "Madhubani Art": "madhubani",
  "Wooden Crafts": "wooden",
  "Wooden Sculptures": "wooden",
  "Sabai Grass": "sabai",
  "Home Decor": "home",
  "Home Decor & Living": "home",
  "Home & Kitchen": "home",
  "Home Décor": "home",
  Dokra: "dokra",
  "Dokra Metal Art": "dokra",
};

export const defaultCraftStory: CraftStoryContent = {
  tagline: "Craft details you can actually notice",
  story:
    "These pieces come from small workshop production, so slight variation in finish, shape, and texture is part of what makes them feel made instead of manufactured.",
  origin: "Small craft workshops across India",
  tradition:
    "Many of these techniques are still learned through practice at home and in local workshop networks.",
  categoryLine:
    "Small-batch craft work where variation is part of the object, not a defect.",
  teaser: "Small-batch work made by hand.",
  icon: "CRAFT",
};

export const getCraftStory = (value?: string | null): CraftStoryContent => {
  if (!value) return defaultCraftStory;

  const slugMatch = slugAliases[value.toLowerCase()];
  if (slugMatch) return craftStoryBySlug[slugMatch];

  const nameMatch = nameAliases[value];
  if (nameMatch) return craftStoryBySlug[nameMatch];

  return defaultCraftStory;
};
